import React, {useState, useRef, useLayoutEffect} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {createPopper} from '@popperjs/core';

// Hover tooltip rendered via a portal so it renders on top and avoids
// overarching DOM elements with overflow: hidden (Cards for example)
function HoverTooltip({tooltip, placement = 'bottom', children}) {
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // useLayoutEffect so Popper measures and positions the bubble before the browser paints
  useLayoutEffect(() => {
    if (!visible || !anchorRef.current || !bubbleRef.current) return;

    const popper = createPopper(anchorRef.current, bubbleRef.current, {
      placement,
      // 'fixed' keeps positioning viewport-relative
      strategy: 'fixed',
      modifiers: [
        {name: 'offset', options: {offset: [0, 6]}},
        // Shifts the bubble inward when it would otherwise spill past a
        // viewport edge (e.g. badge near the right side of the screen).
        {name: 'preventOverflow', options: {padding: 8}},
        // Flips the bubble to the opposite side when there isn't room on the
        // preferred one (e.g. badge near the bottom of the viewport flips up).
        {name: 'flip'}
      ]
    });

    return () => popper.destroy();
  }, [visible, placement]);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const portalRoot = typeof document !== 'undefined' ? document.getElementById('popover-root') : null;

  return (
    <>
      {/* onFocus/onBlur give keyboard users the same affordance as mouse users. */}
      <Anchor ref={anchorRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        {children}
      </Anchor>
      {visible && portalRoot && tooltip && ReactDOM.createPortal(
        <PopupBubble ref={bubbleRef}>{tooltip}</PopupBubble>,
        portalRoot
      )}
    </>
  );
}

const Anchor = styled.span`
  display: inline-flex;
  align-items: center;
`;

const PopupBubble = styled.div`
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: normal;
  text-decoration: none;
  max-width: 320px;
  white-space: normal;
  word-wrap: break-word;
  z-index: 10000;
  /* pointer-events: none prevents the bubble from intercepting the mouse —
     otherwise moving onto the bubble would leave the anchor and flicker. */
  pointer-events: none;
`;

export default HoverTooltip;